import React, { useRef, useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { IconButton, Box, Card, Avatar, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { s3baseUrl, clientPortalUrl } from "src/config/config";
import Label from "src/components/Label";
import CustomPopoverSection from "src/components/MenuOption/CustomPopoverSection";
import CustomConfirmation from "src/components/ModalPopover/Confirmation";
import { useContentSetting } from "src/Hooks/ContentSettingState";
import ProgrammeListMemberProfile from "./ProgrammeListMemberProfile";
import {
  memberAllDetailApi,
  memberDetailApi,
  DeleteMemberApi,
  member_login_api,
} from "src/DAL/member/member";
import { useSnackbar } from "notistack";
import { CircularProgress } from "@mui/material";
import { makeStyles } from "@mui/styles";
import RecordNotFound from "src/components/RecordNotFound";
import { dummyImage } from "src/assets";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import moment from "moment/moment";
import GeneralCalendarClient from "src/components/GeneralComponents/GeneralCalendarClient";
import ReviewDateMemberModal from "src/components/GeneralComponents/ReviewDateMemberModal";

const useStyles = makeStyles(() => ({
  loading: {
    marginLeft: "50%",
    marginTop: "20%",
  },
}));

const get_view_dates = (calendarValue, referenceDate) => {
  let tool = calendarValue;
  const base = referenceDate ? moment(referenceDate) : moment();
  const startOfMonth = base.clone().startOf(tool);
  const daysToSubtract = startOfMonth.day() - 0;
  const previousSunday = startOfMonth.subtract(daysToSubtract, "days");
  let previousSundayDate = previousSunday.toDate();
  let add_days = 42;
  if (tool == "week") {
    add_days = 6;
  } else if (tool == "day") {
    add_days = 0;
  }
  let nextSaturday = startOfMonth.add(add_days, "days");
  let nextSaturdayDate = nextSaturday.toDate();
  if (tool == "day") {
    previousSundayDate = referenceDate ? new Date(referenceDate) : new Date();
    nextSaturdayDate = referenceDate ? new Date(referenceDate) : new Date();
  }

  return {
    start_date: previousSundayDate,
    end_date: nextSaturdayDate,
  };
};

const MemberProfile = () => {
  const member_id = useParams();
  const classes = useStyles();
  const calendarRef = useRef();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const {
    setIsOpenNotePad,
    setSelectedNotepad,
    setIsOpenFormsList,
    setSelectedFormsMember,
  } = useContentSetting();

  // If URL has a valid date param (e.g. shared link), initialize calendar to that month
  const urlDate = searchParams.get("date");
  const isValidUrlDate =
    urlDate && moment(urlDate, "YYYY-MM-DD", true).isValid();
  const [currentDate, setCurrentDate] = useState(
    get_view_dates("month", isValidUrlDate ? urlDate : undefined),
  );
  const [isPasted, setIsPasted] = useState(false);
  const [value, setValue] = React.useState("1");
  const [manualLoading, setManualLoading] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteDoc, setDeleteDoc] = useState("");
  const [openReviewDateModal, setOpenReviewDateModal] = useState(false);
  const reloadRequestRef = useRef({
    id: null,
    message: null,
    skipScroll: false,
    scrollY: 0,
    calScrolls: null,
  });

  useEffect(() => {
    const modalParam = searchParams.get("modal");
    if (modalParam === "add" || modalParam === "edit") {
      setValue("2");
    }
  }, [searchParams]);

  const goToToday = () => {
    if (calendarRef?.current) {
      const calendarApi = calendarRef.current.getApi();
      const today = new Date();
      const viewStart = calendarApi.view.currentStart;
      const viewMonth = viewStart.getMonth();
      const viewYear = viewStart.getFullYear();

      const todayMonth = today.getMonth();
      const todayYear = today.getFullYear();

      const isSameMonth = viewMonth === todayMonth && viewYear === todayYear;

      if (!isSameMonth) {
        calendarApi.gotoDate(today);
        const newDateRange = {
          start_date: calendarApi.view.activeStart,
          end_date: calendarApi.view.activeEnd,
        };
        setCurrentDate(newDateRange);
      } else {
        const todayDate = today.toISOString().slice(0, 10);
        const todayCell = document.querySelector(
          `.fc-day[data-date="${todayDate}"]`,
        );
        if (todayCell) {
          todayCell.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    }
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleScrollCalled = (data) => {
    const todayCell = document.querySelector(
      `.calendar-workout-card[id="${data}"]`,
    );
    if (todayCell)
      todayCell.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const getupdatedData = (workouts) => {
    return workouts.map((workout) => ({
      ...workout,
      exercises: workout.exercises?.map((exercise) => ({
        ...exercise,
        sets: exercise.sets?.map((set) => ({
          ...set,
          vdot: null,
          pace_low: 0,
          pace_up: 423,
        })),
        sub_exercises: exercise.sub_exercises?.map((subExercise) => ({
          ...subExercise,
          sets: subExercise.sets?.map((set) => ({
            ...set,
            vdot: null,
            pace_low: 0,
            pace_up: 423,
          })),
        })),
      })),
    }));
  };

  const prefetchAdjacentMonths = async () => {
    if (!calendarRef?.current) return;
    const calendarApi = calendarRef.current.getApi();
    const originalDate = calendarApi.getDate();

    const nextMonthStart = moment(originalDate)
      .add(1, "month")
      .startOf("month");
    const prevMonthStart = moment(originalDate)
      .subtract(1, "month")
      .startOf("month");

    const nextRange = get_view_dates("month", nextMonthStart.toISOString());
    const prevRange = get_view_dates("month", prevMonthStart.toISOString());

    const nextPostData = {
      start_date: moment(nextRange.start_date).format("YYYY-MM-DD"),
      end_date: moment(nextRange.end_date).format("YYYY-MM-DD"),
      program: "",
    };

    const prevPostData = {
      start_date: moment(prevRange.start_date).format("YYYY-MM-DD"),
      end_date: moment(prevRange.end_date).format("YYYY-MM-DD"),
      program: "",
    };

    const nextDateKey = `${nextPostData.start_date}_${nextPostData.end_date}`;
    const prevDateKey = `${prevPostData.start_date}_${prevPostData.end_date}`;

    try {
      await Promise.all([
        queryClient.prefetchQuery({
          queryKey: ["memberCalendar", member_id.id, nextDateKey],
          queryFn: async () => {
            let type = "calendar";
            const result = await memberDetailApi(
              member_id.id,
              nextPostData,
              type,
            );
            if (result.code == 200) {
              const mergedData = [
                ...result?.workouts,
                ...result?.program_workouts.filter(
                  (item) =>
                    !result?.workouts.find(
                      (existingItem) => existingItem.workout_id === item._id,
                    ),
                ),
              ];
              const processedData = getupdatedData(mergedData);
              return {
                reorderedWorkouts: result?.reordered_workouts,
                workouts: processedData,
                time_zone: result.time_zone,
                disabledDifficultyPrograms: result?.disabledDifficultyPrograms,
                memberInfo: result?.member_info || {},
              };
            }
            return [];
          },
        }),
        queryClient.prefetchQuery({
          queryKey: ["memberCalendar", member_id.id, prevDateKey],
          queryFn: async () => {
            let type = "calendar";
            const result = await memberDetailApi(
              member_id.id,
              prevPostData,
              type,
            );
            if (result.code == 200) {
              const mergedData = [
                ...result?.workouts,
                ...result?.program_workouts.filter(
                  (item) =>
                    !result?.workouts.find(
                      (existingItem) => existingItem.workout_id === item._id,
                    ),
                ),
              ];
              const processedData = getupdatedData(mergedData);
              return {
                reorderedWorkouts: result?.reordered_workouts,
                workouts: processedData,
                time_zone: result.time_zone,
                disabledDifficultyPrograms: result?.disabledDifficultyPrograms,
                memberInfo: result?.member_info || {},
              };
            }
            return [];
          },
        }),
      ]);
    } catch (error) {
      console.log("Prefetch error:", error);
    }
  };

  const { data: memberData, isLoading: isMemberLoading } = useQuery({
    queryKey: ["memberDetail", member_id.id],
    keepPreviousData: true,
    queryFn: async () => {
      const result = await memberAllDetailApi(member_id.id);
      if (result.code == 200) {
        localStorage.setItem(
          "day_calories",
          JSON.stringify({
            low_day_calories: result?.member?.low_day_calories,
            high_day_calories: result?.member?.high_day_calories,
            regular_day_calories: result?.member?.regular_day_calories,
            deficit_percentage: result?.member?.deficit_percentage,
            deficit_value: result?.member?.deficit_value,
            kcal_type: result?.member?.kcal_type,
            high_day_carb: result?.member?.high_day_carb,
            high_day_fat: result?.member?.high_day_fat,
            high_day_protein: result?.member?.high_day_protein,
            low_day_carb: result?.member?.low_day_carb,
            low_day_fat: result?.member?.low_day_fat,
            low_day_protein: result?.member?.low_day_protein,
            gender: result?.member?.gender || "",
            is_menstrual_cycle: result?.member?.is_menstrual_cycle || "",
            permission_cycle: result?.member?.permission_cycle || "",
          }),
        );
        return result;
      } else {
        enqueueSnackbar(result.message, { variant: "error" });
      }
    },
  });

  const memberAllDetails = memberData?.member;
  const memberPoints = memberData?.reward_points;

  const currentDateKey = `${moment(currentDate.start_date).format(
    "YYYY-MM-DD",
  )}_${moment(currentDate.end_date).format("YYYY-MM-DD")}`;

  const {
    data: calendarData,
    isLoading: isCalendarLoading,
    refetch,
  } = useQuery({
    queryKey: ["memberCalendar", member_id.id, currentDateKey],
    keepPreviousData: true,
    queryFn: async () => {
      let apiData = {
        start_date: moment(currentDate.start_date).format("YYYY-MM-DD"),
        end_date: moment(currentDate.end_date).format("YYYY-MM-DD"),
        program: "",
      };
      let type = "calendar";
      setManualLoading(true);
      const result = await memberDetailApi(member_id.id, apiData, type);

      if (result.code == 200) {
        setManualLoading(false);
        const mergedData = [
          ...result?.workouts,
          ...result?.program_workouts.filter(
            (item) =>
              !result?.workouts.find(
                (existingItem) => existingItem.workout_id === item._id,
              ),
          ),
        ];

        const processedData = getupdatedData(mergedData);
        return {
          reorderedWorkouts: result?.reordered_workouts,
          workouts: processedData,
          time_zone: result.time_zone,
          disabledDifficultyPrograms: result?.disabledDifficultyPrograms,
          memberInfo: result?.member_info || {},
        };
      } else {
        setManualLoading(false);
        setIsPasted(false);
        enqueueSnackbar(result.message, { variant: "error" });
      }
    },
    onSuccess: () => {
      prefetchAdjacentMonths();
      setIsPasted(false);
      const { id, message, skipScroll } = reloadRequestRef.current;
      if (message) {
        enqueueSnackbar(message, { variant: "success" });
      }

      if (!skipScroll) {
        if (id) {
          setTimeout(() => handleScrollCalled(id), 100);
        } else {
          // setTimeout(() => {
          //   const today = new Date();
          //   const todayDate = today.toISOString().slice(0, 10);
          //   const todayCell = document.querySelector(
          //     `.fc-day[data-date="${todayDate}"]`,
          //   );
          //   if (todayCell) {
          //     todayCell.scrollIntoView({ behavior: "smooth", block: "center" });
          //   }
          // }, 100);
        }
      } else {
        const savedScrollY = reloadRequestRef.current.scrollY;
        const savedCalScrolls = reloadRequestRef.current.calScrolls;
        requestAnimationFrame(() => {
          window.scrollTo({ top: savedScrollY, behavior: "instant" });
          if (savedCalScrolls) {
            const scrollers = document.querySelectorAll(".fc-scroller");
            savedCalScrolls.forEach(({ index, top, left }) => {
              const el = scrollers[index];
              if (el) {
                el.scrollTop = top;
                el.scrollLeft = left;
              }
            });
          }
        });
      }
      reloadRequestRef.current = {
        id: null,
        message: null,
        skipScroll: false,
        scrollY: 0,
        calScrolls: null,
      };
    },
    onError: (error) => {
      setIsPasted(false);
      setManualLoading(false);
    },
  });

  const workouts = calendarData?.workouts || [];
  const reorderWorkouts = calendarData?.reorderedWorkouts || [];
  const defaultTimeValue = calendarData?.time_zone || "Asia/Karachi";
  const difficultyProgram = calendarData?.disabledDifficultyPrograms || [];
  const memberInfo = calendarData?.memberInfo || {};

  const memberDetailInfo = async (
    reloadId,
    message,
    setOpenCopyPasteConfig,
    skipScroll = false,
  ) => {
    if (setOpenCopyPasteConfig) {
      setOpenCopyPasteConfig(false);
    }

    let calScrolls = null;
    if (skipScroll) {
      const scrollers = document.querySelectorAll(".fc-scroller");
      calScrolls = Array.from(scrollers).map((el, i) => ({
        index: i,
        top: el.scrollTop,
        left: el.scrollLeft,
      }));
    }

    reloadRequestRef.current = {
      id: reloadId,
      message: message,
      skipScroll: skipScroll,
      scrollY: skipScroll ? window.scrollY : 0,
      calScrolls,
    };
    refetch();
  };

  const handleReviewDateSuccess = ({ changed }) => {
    queryClient.setQueryData(["memberDetail", member_id.id], (old) => {
      if (!old) return old;
      return {
        ...old,
        member: {
          ...old.member,
          program: old.member.program.map((p) => {
            const progId = p._id?._id;
            const updated = changed.find((c) => c._id === progId);
            if (updated) {
              return {
                ...p,
                review_date: updated.review_date,
                use_individual_review_date: updated.use_individual_review_date,
              };
            }
            return p;
          }),
        },
      };
    });
  };

  // ─── Action handlers for 3-dot menu ────────────────────────────────────────

  const handleEdit = (member) => {
    navigate(`/member/edit-member/${member._id}`, { state: member });
  };

  const handleOpenNotePad = (member) => {
    const hasPrograms = member?.program?.length > 0;
    const fullName = `${member.first_name} ${member.last_name}`;
    const baseData = { title: fullName, images: null, member_id: member._id };

    if (hasPrograms) {
      setSelectedNotepad({
        ...baseData,
        _id: member.program[0]?._id?._id,
        program_list: member.program,
        message:
          "This notepad is specific to this member for the selected programme.",
        type_member: true,
      });
    } else {
      setSelectedNotepad({
        ...baseData,
        message:
          "This notepad is specific to this member only. (Member's programming notepad)",
      });
    }
    setIsOpenNotePad(true);
  };

  const handleLoginAsMember = async (member) => {
    const result = await member_login_api({
      email: member.email,
      user_type: "client",
    });
    if (result.code === 200) {
      window.open(`${clientPortalUrl}login-by-admin/${result.token}`);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
    }
  };

  const handleChat = (member) => {
    navigate(`/chat/?chat_id=${member._id}`, { state: member });
  };

  const handleWeightTracker = (member) => {
    navigate(`/member/weight-tracker/${member._id}`, { state: member });
  };

  const handleFormList = (member) => {
    setSelectedFormsMember(member);
    setIsOpenFormsList(true);
  };

  const handlePoints = (member) => {
    navigate(`/member/member-points/${member._id}`, { state: member });
  };

  const handleMemberSubscribers = (member) => {
    navigate(`/member/member-subscribers/${member._id}`, { state: member });
  };

  const handleAgreeDelete = (member) => {
    setDeleteDoc(member._id);
    setOpenDelete(true);
  };

  const handleChangePassword = (member) => {
    navigate(`/member/change-password/${member._id}`, { state: member });
  };

  const handleOpenReviewDate = () => {
    setOpenReviewDateModal(true);
  };

  const handleDelete = async () => {
    setOpenDelete(false);
    const result = await DeleteMemberApi(deleteDoc);
    if (result.code === 200) {
      enqueueSnackbar(result.message, { variant: "success" });
      navigate(-1);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
    }
  };

  const MENU_OPTIONS = [
    {
      label: "Edit",
      icon: "akar-icons:edit",
      handleClick: handleEdit,
    },
    {
      label: "Notepad",
      icon: "tabler:notebook",
      handleClick: handleOpenNotePad,
    },
    {
      label: "Review Date",
      icon: "mdi:calendar-check",
      handleClick: handleOpenReviewDate,
    },
    {
      label: "Login As Member",
      icon: "basil:login-solid",
      handleClick: handleLoginAsMember,
    },
    {
      label: "Chat",
      icon: "fluent:chat-28-filled",
      handleClick: handleChat,
    },
    {
      label: "Weight Tracker",
      icon: "mdi:weight-lifter",
      handleClick: handleWeightTracker,
    },
    {
      label: "View Forms",
      icon: "fluent:form-48-filled",
      handleClick: handleFormList,
    },
    {
      label: "View Points History",
      icon: "game-icons:two-coins",
      handleClick: handlePoints,
    },
    {
      label: "View Subscriptions",
      icon: "eva:eye-fill",
      handleClick: handleMemberSubscribers,
    },
    {
      label: "Delete",
      icon: "ant-design:delete-twotone",
      handleClick: handleAgreeDelete,
    },
    {
      label: "Change Password",
      icon: "ant-design:lock",
      handleClick: handleChangePassword,
    },
  ];

  const isLoading = isMemberLoading || isCalendarLoading;

  const calendarProgramme = useMemo(
    () => ({ ...memberAllDetails, difficult_program: difficultyProgram }),
    [memberAllDetails?._id, difficultyProgram],
  );

  if (isLoading) {
    return <CircularProgress className={classes.loading} color="primary" />;
  }

  return (
    <div className="container-fluid">
      <CustomConfirmation
        open={openDelete}
        setOpen={setOpenDelete}
        title="Are you sure you want to delete?"
        handleAgree={handleDelete}
      />

      {memberAllDetails && (
        <ReviewDateMemberModal
          member={{
            ...memberAllDetails,
            programs: memberAllDetails?.program
              ?.filter((p) => p._id !== null)
              .map((p) => ({ ...p, _id: p._id._id, title: p._id.title })),
          }}
          isOpenPop={openReviewDateModal}
          isClosePop={() => setOpenReviewDateModal(false)}
          onSuccess={handleReviewDateSuccess}
        />
      )}

      {/* ── Page header ─────────────────────────────────────────────────────── */}
      <div className="row mb-3 mt-2">
        <div className="col-12">
          <h2>
            <IconButton
              className="back-screen-button m-0"
              onClick={() => navigate(-1)}
            >
              <ArrowBackIcon className="me-2" />
            </IconButton>
            Member Profile
          </h2>
        </div>
      </div>

      {/* ── Single profile card ───────────────────────────────────────────── */}
      <Card
        sx={{
          borderRadius: 2,
          mb: 3,
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          overflow: "hidden",
        }}
      >
        {/* Top: avatar + name + actions */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: 3,
            py: 2,
            borderBottom: "1px solid #f0f0f0",
          }}
        >
          <Avatar
            alt={`${memberAllDetails?.first_name} ${memberAllDetails?.last_name}`}
            src={
              memberAllDetails?.profile_image
                ? s3baseUrl + memberAllDetails.profile_image
                : dummyImage
            }
            sx={{ width: 64, height: 64 }}
          />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              {memberAllDetails?.first_name} {memberAllDetails?.last_name}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", fontSize: 13 }}
            >
              {memberAllDetails?.email}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography variant="caption" sx={{ color: "text.disabled" }}>
              Created:{" "}
              {memberAllDetails?.createdAt
                ? moment(memberAllDetails.createdAt).format("DD MMM YYYY")
                : "—"}
            </Typography>
            <CustomPopoverSection menu={MENU_OPTIONS} data={memberAllDetails} />
          </Box>
        </Box>

        {/* Info grid — 2 columns, main fields only */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          }}
        >
          {[
            {
              label: "Status",
              value: (
                <Label
                  variant="ghost"
                  color={
                    memberAllDetails?.status === false ? "error" : "success"
                  }
                >
                  {memberAllDetails?.status === false ? "InActive" : "Active"}
                </Label>
              ),
            },
            {
              label: "Total Points",
              value:
                memberAllDetails?.total_points ??
                memberPoints?.total_points ??
                0,
            },
            {
              label: "Harmony Member",
              value: (
                <Label
                  variant="ghost"
                  color={
                    memberAllDetails?.is_harmony_member ? "success" : "error"
                  }
                >
                  {memberAllDetails?.is_harmony_member ? "Yes" : "No"}
                </Label>
              ),
            },
            {
              label: "Pace Calculator",
              value: (
                <Label
                  variant="ghost"
                  color={
                    memberAllDetails?.pace_calculator ? "success" : "error"
                  }
                >
                  {memberAllDetails?.pace_calculator ? "Enabled" : "Disabled"}
                </Label>
              ),
            },
            {
              label: "Gender",
              value: memberAllDetails?.gender
                ? memberAllDetails.gender.charAt(0).toUpperCase() +
                  memberAllDetails.gender.slice(1)
                : "—",
            },
            {
              label: "Date of Birth",
              value: memberAllDetails?.dob
                ? moment(memberAllDetails.dob).format("DD MMM YYYY")
                : "—",
            },
            {
              label: "Contact Number",
              value: memberAllDetails?.contact_number || "—",
            },
            {
              label: "Address",
              value:
                [
                  memberAllDetails?.street,
                  memberAllDetails?.city,
                  memberAllDetails?.state,
                  memberAllDetails?.zip_code,
                  memberAllDetails?.country,
                ]
                  .filter(Boolean)
                  .join(", ") || "—",
            },
            {
              label: "Created By",
              value: memberAllDetails?.created_by
                ? memberAllDetails.created_by.charAt(0).toUpperCase() +
                  memberAllDetails.created_by.slice(1)
                : "—",
            },
          ].map((row, i, arr) => (
            <Box
              key={row.label}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2.5,
                py: 1.1,
                borderBottom:
                  i < arr.length - 2
                    ? "1px solid #f0f0f0"
                    : { xs: "1px solid #f0f0f0", sm: "none" },
                borderRight:
                  i % 2 === 0
                    ? { xs: "none", sm: "1px solid #f0f0f0" }
                    : "none",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", fontSize: 13 }}
              >
                {row.label}
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, fontSize: 13 }}
              >
                {row.value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Card>

      <Box sx={{ width: "100%", typography: "body1" }}>
        <TabContext value={value}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <TabList onChange={handleChange} aria-label="lab API tabs example">
              <Tab label="Programs" value="1" />
              <Tab label="Programming" value="2" />
            </TabList>
          </Box>
          <TabPanel value="1" style={{ padding: "0px" }}>
            <div className="row mt-3">
              {memberAllDetails?.program.length < 1 ? (
                <>
                  <h2>Programmes</h2>
                  <RecordNotFound title="Programmes" />
                </>
              ) : (
                <ProgrammeListMemberProfile
                  programmes={memberAllDetails?.program.filter(
                    (item) => item._id !== null,
                  )}
                  Id={member_id.id}
                  member_vdot={memberAllDetails?.vdot?.vdot_value}
                  memberAllDetails={memberAllDetails}
                />
              )}
            </div>
          </TabPanel>
          <TabPanel value="2" className="mt-3 p-0">
            <GeneralCalendarClient
              programWorkoutList={workouts}
              reorderedWorkoutList={reorderWorkouts}
              setCurrentDate={setCurrentDate}
              showAddEvent={true}
              time_zone={defaultTimeValue}
              currentDate={currentDate}
              isLoading={isLoading}
              setIsLoading={() => {}}
              reloadList={memberDetailInfo}
              calendar_title="Calendar"
              programme={calendarProgramme}
              member_id={member_id.id}
              member_vdot={
                memberAllDetails?.pace_calculator
                  ? memberAllDetails?.vdot?.vdot_value
                  : null
              }
              member_vdot_info={
                memberAllDetails?.pace_calculator
                  ? memberAllDetails?.vdot
                  : null
              }
              isPasted={isPasted}
              setIsPasted={setIsPasted}
              difficultyProgram={difficultyProgram}
              calendarRef={calendarRef}
              goToToday={goToToday}
              memberInfo={memberInfo}
              isMemberCalendar={true}
              manualLoading={manualLoading}
              initialDate={isValidUrlDate ? urlDate : undefined}
            />
          </TabPanel>
        </TabContext>
      </Box>
    </div>
  );
};

export default MemberProfile;
